import React, { useEffect } from 'react';
import { usePeriodoForm } from '../hooks/usePeriodoForm';
import { FormInput, FormSelect } from '../components/form/FormFields';

// ============================================================
// PeriodoFormPage – Registro / Modificación de Periodo
// ============================================================

interface Props {
  idPeriodo?: number;
  onSuccess?: () => void;
  onCancel?:  () => void;
}

const SpinnerIcon = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const PeriodoFormPage: React.FC<Props> = ({ idPeriodo, onSuccess = () => {}, onCancel = () => {} }) => {
  const isEditing = idPeriodo !== undefined;

  const {
    form, errors, submitting, submitError, loadingInit,
    añosEscolares,
    handleChange, handleSubmit, loadPeriodo,
  } = usePeriodoForm(idPeriodo ?? null, onSuccess);

  useEffect(() => { loadPeriodo(); }, [loadPeriodo]);

  if (loadingInit) {
    return (
      <div className="flex justify-center items-center h-64">
        <SpinnerIcon />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">

      {/* Cabecera */}
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onCancel}
          className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            {isEditing ? 'Modificar Periodo' : 'Registrar Periodo'}
          </h1>
          <p className="text-slate-400 text-sm">
            {isEditing ? 'Actualiza los datos del periodo' : 'Completa los datos para crear un nuevo periodo'}
          </p>
        </div>
      </div>

      {/* Tarjeta del formulario */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <h2 className="text-white font-bold">Datos del Periodo</h2>
          <p className="text-blue-200 text-xs mt-0.5">
            Información del periodo académico
          </p>
        </div>

        <div className="p-6 space-y-4">

          <FormSelect
            label="Año Escolar"
            name="anioEscolarId"
            value={form.anioEscolarId}
            onChange={handleChange}
            error={errors.anioEscolarId}
            required
            placeholder="Seleccione un año escolar"
            options={añosEscolares.map(a => ({ value: a.idAnioEscolar, label: String(a.anio) }))}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormInput
              label="Número"
              name="numero"
              type="number"
              value={form.numero}
              onChange={handleChange}
              error={errors.numero}
              required
              placeholder="Ej: 1"
              min={1}
            />

            <FormInput
              label="Nombre"
              name="nombre"
              value={form.nombre}
              onChange={handleChange}
              error={errors.nombre}
              required
              placeholder="Ej: Bimestre I"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormInput
              label="Fecha de Inicio"
              name="fechaInicio"
              type="date"
              value={form.fechaInicio}
              onChange={handleChange}
              error={errors.fechaInicio}
              required
            />

            <FormInput
              label="Fecha de Fin"
              name="fechaFin"
              type="date"
              value={form.fechaFin}
              onChange={handleChange}
              error={errors.fechaFin}
              required
            />
          </div>

          {/* Error de envío */}
          {submitError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
              </svg>
              <span className="text-red-600 text-sm">{submitError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <button onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold
                       bg-green-600 hover:bg-green-700 text-white transition-colors shadow-sm disabled:opacity-60">
            {submitting ? <SpinnerIcon /> : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
            )}
            {isEditing ? 'Guardar Cambios' : 'Registrar Periodo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PeriodoFormPage;
